import * as Y from "yjs";

/**
 * Root manifest helpers for `sectioned-v1` documents.
 *
 * The root Y.Doc holds a single Y.Array of Y.Doc subdocument references under
 * {@link SECTION_SUBDOCS_KEY}. Section bodies live in their own Y.Doc, their own
 * DB row and their own Hocuspocus room, so the root only ever carries ordering
 * and identity - never section content.
 */
export const SECTION_SUBDOCS_KEY = "sections";

/** Thrown when a manifest operation names a section the root does not hold. */
export class UnknownSectionError extends Error {
  constructor(sectionId: string) {
    super(`Section ${sectionId} is not referenced by the root manifest`);
    this.name = "UnknownSectionError";
  }
}

/** Thrown when a manifest operation would break the manifest invariants. */
export class InvalidSectionManifestError extends Error {
  constructor(reason: string) {
    super(`Invalid section manifest operation: ${reason}`);
    this.name = "InvalidSectionManifestError";
  }
}

/** The ordered subdocument references of a root manifest document. */
export function sectionReferences(root: Y.Doc): Y.Array<Y.Doc> {
  return root.getArray<Y.Doc>(SECTION_SUBDOCS_KEY);
}

/**
 * Creates the subdocument reference for `sectionId`. The GUID is the section
 * UUID, which is what both the section room name and the DB row key derive from.
 */
export function createSectionSubdocument(sectionId: string): Y.Doc {
  if (sectionId.length === 0) {
    throw new InvalidSectionManifestError("sectionId must not be empty");
  }
  return new Y.Doc({ guid: sectionId });
}

/** The section ids in manifest order. */
export function orderedSectionIds(root: Y.Doc): string[] {
  return sectionReferences(root)
    .toArray()
    .map((section) => section.guid);
}

/** Appends a subdocument reference to the end of the manifest. */
export function appendSection(root: Y.Doc, section: Y.Doc): void {
  const sections = sectionReferences(root);
  if (indexOfSection(sections, section.guid) !== -1) {
    throw new InvalidSectionManifestError(`section ${section.guid} is already referenced`);
  }
  sections.push([section]);
}

/**
 * Drops a subdocument reference from the manifest. The DB row is soft-deleted
 * separately by the section delete API; this only stops referencing it.
 */
export function removeSection(root: Y.Doc, sectionId: string): void {
  const sections = sectionReferences(root);
  sections.delete(requireSectionIndex(sections, sectionId), 1);
}

/**
 * Moves a section to `toIndex` inside one transaction.
 *
 * Y.Array has no move primitive and Yjs refuses to re-integrate an already
 * integrated subdocument, so the reference is replaced by a fresh Y.Doc with the
 * same GUID. Consumers must therefore reconcile providers by GUID rather than by
 * Y.Doc object identity.
 */
export function moveSection(root: Y.Doc, sectionId: string, toIndex: number): void {
  const sections = sectionReferences(root);
  const fromIndex = requireSectionIndex(sections, sectionId);
  if (!Number.isInteger(toIndex) || toIndex < 0 || toIndex >= sections.length) {
    throw new InvalidSectionManifestError(`target index ${toIndex} is out of range`);
  }
  if (fromIndex === toIndex) return;

  root.transact(() => {
    sections.delete(fromIndex, 1);
    sections.insert(toIndex, [createSectionSubdocument(sectionId)]);
  });
}

function requireSectionIndex(sections: Y.Array<Y.Doc>, sectionId: string): number {
  const index = indexOfSection(sections, sectionId);
  if (index === -1) throw new UnknownSectionError(sectionId);
  return index;
}

function indexOfSection(sections: Y.Array<Y.Doc>, sectionId: string): number {
  return sections.toArray().findIndex((section) => section.guid === sectionId);
}
